import { Address, AddressCustomFields } from 'shared/types/account/Address';
import { Cart, ShippingMethod } from 'shared/types/cart';
import { Money } from 'shared/types/product/Money';
import { zoneToCountryMapping } from 'helpers/countryHelpers';
import { CurrencyHelpers } from 'helpers/currencyHelpers';

const applePayNewTotal = (res: Cart, locale: any, payLabel: string): ApplePayJS.ApplePayLineItem => {
  return {
    label: payLabel,
    amount:
      res !== undefined && res.sum !== undefined && res.sum.centAmount !== undefined
        ? CurrencyHelpers.formatMoney(res.sum).toString()
        : '0',
    type: 'final' as ApplePayJS.ApplePayLineItemType,
  };
};

const applepayLineItems = (
  transaction: {
    subtotal: Required<Money>;
    discount: Required<Money>;
    shipping: Required<Money>;
    tax: Required<Money>;
    total: Required<Money>;
  },
  res: Cart,
  locale: any,
): ApplePayJS.ApplePayLineItem[] => {
  return [
    {
      label: 'Merchandise',
      amount: CurrencyHelpers.formatMoney(transaction.subtotal).toString(),
      type: 'final' as ApplePayJS.ApplePayLineItemType,
    },
    {
      label: res?.shippingInfo?.name !== undefined ? res?.shippingInfo?.name : '',
      amount:
        res.shippingInfo !== undefined &&
        res.shippingInfo?.price !== undefined &&
        res.shippingInfo?.price?.centAmount !== undefined
          ? CurrencyHelpers.formatMoney(res.shippingInfo?.price).toString()
          : '0',
      type: 'final' as ApplePayJS.ApplePayLineItemType,
    },
    {
      label: 'Tax',
      amount:
        res.taxed !== undefined && res.taxed.amount !== undefined && res.taxed.amount.centAmount !== undefined
          ? CurrencyHelpers.formatMoney(res?.taxed?.amount).toString()
          : '0',
      type: 'final' as ApplePayJS.ApplePayLineItemType,
    },
    {
      label: 'Discount',
      amount:
        res.discountOnTotalPrice !== undefined && res.discountOnTotalPrice.centAmount !== undefined
          ? CurrencyHelpers.formatMoney(res?.discountOnTotalPrice).toString()
          : '0',
      type: 'final' as ApplePayJS.ApplePayLineItemType,
    },
  ];
};

const validateCartForRestrictedItems = (res: Cart, initialAddressData: Address): boolean => {
  const isItemRestricted = res.lineItems?.some((item) => {
    return item?.variant?.attributes?.exclusion?.some((exclusion: any) => {
      const exclusionLabel = exclusion.label as keyof typeof zoneToCountryMapping;
      const country = initialAddressData.country || '';

      return zoneToCountryMapping[exclusionLabel]?.includes(country);
    });
  });

  return !!isItemRestricted;
};

const validateShippingMethod = (res: Cart, shippingMethods: ShippingMethod[]): boolean => {
  const isShippingMethodValid = shippingMethods.find(
    (shippingMethods) => res.shippingInfo?.shippingMethodId == shippingMethods.shippingMethodId,
  );
  return !!isShippingMethodValid;
};

const validateAddress = (
  initialAddressData: Address,
): { field: ApplePayJS.ApplePayErrorContactField; message: string }[] => {
  const errors: { field: ApplePayJS.ApplePayErrorContactField; message: string }[] = [];

  const fieldsToValidate = [
    {
      field: 'name' as ApplePayJS.ApplePayErrorContactField,
      feildName: 'firstName',
      message: 'First name is required',
    },
    { field: 'name' as ApplePayJS.ApplePayErrorContactField, feildName: 'lastName', message: 'Last name is required' },
    {
      field: 'addressLines' as ApplePayJS.ApplePayErrorContactField,
      feildName: 'streetName',
      message: 'Street name is required',
    },
    {
      field: 'postalAddress' as ApplePayJS.ApplePayErrorContactField,
      feildName: 'postalCode',
      message: 'Postal code is required',
    },
    {
      field: 'phoneNumber' as ApplePayJS.ApplePayErrorContactField,
      feildName: 'phone',
      message: 'Phone number is required',
    },
    { field: 'locality' as ApplePayJS.ApplePayErrorContactField, feildName: 'city', message: 'City is required' },
    {
      field: 'country' as ApplePayJS.ApplePayErrorContactField,
      feildName: 'country',
      message: 'Country is required',
    },
  ];

  fieldsToValidate.forEach(({ field, feildName, message }) => {
    if (!validateTextExists(initialAddressData[feildName as keyof Address])) {
      errors.push({ field, message });
    }
  });

  return errors;
};

const validateTextExists = (name: string | undefined | boolean | AddressCustomFields): boolean => {
  return name !== undefined && typeof name === 'string' && name.trim().length >= 1;
};

export { applepayLineItems, applePayNewTotal, validateCartForRestrictedItems, validateShippingMethod, validateAddress };
